import { inject, Injectable } from "@angular/core";
import { EMPTY, map, Observable, switchMap, take, throwError } from "rxjs";
import { CantaraFunctionService, Cc7AuthService, Cc7SessionRepository, FunctionResponse } from "@rinami/cc7-ng";
import { OfflineTransactionType } from "../enums/offline-transaction-type.enum";
import { CCOfflineTransactionStatus } from "../enums/offline-transaction-status.enum";
import { OfflineTransaction } from "../../../models/offline-transaction.model";
import { ConfigService } from "../../config/config.service";
import { OfflinePayload } from "../../../models/offline-payload.model";
import { ApplicationContext } from "../../context/application-context.service";
import { OfflineTransactionDao } from "../../../services/dao/offline-transaction.dao";
import { FunctionRequestable } from "../../network/services/function-requestable";

@Injectable({ providedIn: "root" })
/**
 * Base implementation for offline-capable actions.
 * Subclasses define how a request is queued, replayed, and cleaned up after a successful sync.
 */
export abstract class OfflineWorker<T = any, R = any> {
  protected readonly cantaraFunctionService = inject(CantaraFunctionService);
  protected readonly authService = inject(Cc7AuthService);
  protected readonly configService = inject(ConfigService);
  protected readonly offlineTransactionDao = inject(OfflineTransactionDao);
  protected readonly appContext = inject(ApplicationContext);
  protected readonly sessionRepository = inject(Cc7SessionRepository);

  /**
   * Returns the type of the transaction this worker handles.
   */
  getType(): OfflineTransactionType {
    return OfflineTransactionType.General;
  }

  /**
   * Pre-execution logic that runs before the transaction is saved.
   * Must be implemented by each offline worker.
   * Useful for local storage updates (e.g., creating a local record).
   */
  abstract preExecute(payload: OfflinePayload<R>): Observable<any>;

  /**
   * Performs the actual network request during synchronization.
   * Must be implemented by each offline worker.
   */
  abstract sync(transaction: OfflineTransaction): Observable<any>;

  /**
   * Shared helper for workers that replay generic function requests.
   */
  protected functionResponse(transaction: OfflineTransaction): Observable<FunctionResponse> {
    const request = transaction.payload.request;
    if (!this.isFunctionRequestable(request)) {
      return throwError(() => new Error("Invalid request payload"));
    }
    return this.cantaraFunctionService.executeFunction(this.configService.xCantaraNamespace, request.functionRequest);
  }

  private isFunctionRequestable(value: unknown): value is FunctionRequestable {
    return (
      !!value &&
      typeof value === "object" &&
      "functionName" in value &&
      "functionRequest" in value &&
      typeof (value as FunctionRequestable).functionName === "string" &&
      !!(value as FunctionRequestable).functionRequest
    );
  }

  /**
   * Logic to execute when the network request fails with an OfflineError.
   * Default implementation uses preExecute and saves a transaction.
   *
   * @param {OfflinePayload<R>} payload - The request payload.
   * @param {OfflineError} error - The offline error encountered.
   * @return {Observable<T>} An observable that resolves with the result of the offline operation.
   */
  handleOfflineError(payload: OfflinePayload<R> | R, error: any): Observable<T> {
    return throwError(() => error);
  }

  /**
   * Queues a payload for replay once the app is online again.
   *
   * @param payload The request body that should be persisted for later sync.
   * @param name The readable transaction name shown to the user.
   * @param groupKey Optional parent key used to associate related queued transactions.
   * @return The newly created transaction once it has been persisted.
   */
  addToSyncQueue(payload: OfflinePayload<R>, name: string, groupKey?: string): Observable<OfflineTransaction> {
    return this.sessionRepository.getSession().pipe(
      take(1),
      switchMap(session => {
        const transaction: OfflineTransaction = {
          id: crypto.randomUUID(),
          createdOnDateTimestamp: new Date(),
          transactionName: name,
          status: CCOfflineTransactionStatus.Unsynced,
          createdByUsername: session?.username ?? "",
          payload,
          ...(groupKey && { groupKey }),
          ...(this.getType() && { type: this.getType() }),
        };
        return this.offlineTransactionDao.save([transaction]).pipe(
          switchMap(() => this.appContext.refreshOfflineState()),
          map(() => transaction)
        );
      })
    );
  }
  /**
   * Runs after a successful replay so local state can be updated with the server response.
   *
   * @param response The server response returned by the request.
   * @param transaction The original queued transaction being finalized.
   */
  onSuccess(response: any, transaction?: OfflineTransaction): Observable<void> {
    return EMPTY;
  }

  /**
   * Recursively updates matching values within a payload object so queued requests can be
   * re-bound to real server-generated identifiers before the next replay.
   *
   * @param payload The object tree to traverse.
   * @param oldVal The previous local value to replace.
   * @param newVal The server value to assign in its place.
   */
  protected updatePayload(payload: any, oldVal: string, newVal: string): void {
    if (!payload || typeof payload !== "object") return;

    Object.keys(payload).forEach(key => {
      if (payload[key] === oldVal) {
        payload[key] = newVal;
      } else if (Array.isArray(payload[key])) {
        payload[key].forEach((item: any) => {
          if (item && typeof item === "object") {
            this.updatePayload(item, oldVal, newVal);
          }
        });
      } else if (typeof payload[key] === "object") {
        this.updatePayload(payload[key], oldVal, newVal);
      }
    });

    // Specifically handle parameters array if present (standard for FunctionRequests)
    if (Array.isArray(payload.parameters)) {
      payload.parameters.forEach((p: any) => {
        if (p && p.value === oldVal) {
          p.value = newVal;
        }
      });
    }
  }
}
